import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister, $findMatchingParent } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  $insertNodes,
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW
} from 'lexical';
import { $setBlocksType } from '@lexical/selection';
import { $createQuoteNode, $isQuoteNode, $createHeadingNode, $isHeadingNode } from '@lexical/rich-text';
import { $createCodeNode, $isCodeNode } from '@lexical/code';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode
} from '@lexical/list';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { INSERT_HORIZONTAL_RULE_COMMAND } from '@lexical/react/LexicalHorizontalRuleNode';
import { INSERT_TABLE_COMMAND } from '@lexical/table';
import { $isMarkNode, $wrapSelectionInMarkNode, $unwrapMarkNode } from '@lexical/mark';
import { UploadFile } from 'helpers/fileHelper';
import { $createImageNode } from '../nodes/ImageNode';
import { $createFileLinkNode } from '../nodes/FileLinkNode';

const BLOCK_TYPE_PARAGRAPH = 'paragraph';
const BLOCK_TYPE_H1 = 'h1';
const BLOCK_TYPE_H2 = 'h2';
const BLOCK_TYPE_H3 = 'h3';
const BLOCK_TYPE_QUOTE = 'quote';
const BLOCK_TYPE_CODE = 'code';

const BLOCK_TYPE_OPTIONS = [
  { value: BLOCK_TYPE_PARAGRAPH, label: 'Normal' },
  { value: BLOCK_TYPE_H1, label: 'Heading 1' },
  { value: BLOCK_TYPE_H2, label: 'Heading 2' },
  { value: BLOCK_TYPE_H3, label: 'Heading 3' },
  { value: BLOCK_TYPE_QUOTE, label: 'Quote' },
  { value: BLOCK_TYPE_CODE, label: 'Code Block' }
];

const generateMarkId = () =>
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `mark-${Date.now()}-${Math.random()}`;

const ToolbarButton = ({ active, disabled, onClick, label, title }) => (
  <button
    type="button"
    className={`editor-toolbar-button${active ? ' editor-toolbar-button-active' : ''}`}
    onMouseDown={event => event.preventDefault()}
    onClick={onClick}
    disabled={disabled}
    title={title}
  >
    {label}
  </button>
);

ToolbarButton.propTypes = {
  active: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  label: PropTypes.node,
  title: PropTypes.string
};

const ToolbarDivider = () => <span className="editor-toolbar-divider" />;

const ToolbarPlugin = ({ imageFolder }) => {
  const [editor] = useLexicalComposerContext();
  const fileInputRef = useRef(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isInlineCode, setIsInlineCode] = useState(false);
  const [isSubscript, setIsSubscript] = useState(false);
  const [isSuperscript, setIsSuperscript] = useState(false);
  const [isHighlight, setIsHighlight] = useState(false);
  const [blockType, setBlockType] = useState(BLOCK_TYPE_PARAGRAPH);
  const [isOrderedList, setIsOrderedList] = useState(false);
  const [isUnorderedList, setIsUnorderedList] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [linkPopoverUrl, setLinkPopoverUrl] = useState(null);

  const updateToolbarState = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    setIsBold(selection.hasFormat('bold'));
    setIsItalic(selection.hasFormat('italic'));
    setIsUnderline(selection.hasFormat('underline'));
    setIsStrikethrough(selection.hasFormat('strikethrough'));
    setIsInlineCode(selection.hasFormat('code'));
    setIsSubscript(selection.hasFormat('subscript'));
    setIsSuperscript(selection.hasFormat('superscript'));

    const anchorNode = selection.anchor.getNode();

    const markParent = $findMatchingParent(anchorNode, node => $isMarkNode(node));
    setIsHighlight(!!markParent);

    const listParent = $findMatchingParent(anchorNode, node => $isListNode(node));
    setIsOrderedList(!!listParent && listParent.getListType() === 'number');
    setIsUnorderedList(!!listParent && listParent.getListType() === 'bullet');

    const linkParent = $findMatchingParent(anchorNode, node => $isLinkNode(node));
    setIsLink(!!linkParent);

    if ($findMatchingParent(anchorNode, node => $isCodeNode(node))) {
      setBlockType(BLOCK_TYPE_CODE);
    } else if ($findMatchingParent(anchorNode, node => $isQuoteNode(node))) {
      setBlockType(BLOCK_TYPE_QUOTE);
    } else {
      const headingParent = $findMatchingParent(anchorNode, node => $isHeadingNode(node));
      setBlockType(headingParent ? headingParent.getTag() : BLOCK_TYPE_PARAGRAPH);
    }
  }, []);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({ editorState }) => {
          editorState.read(updateToolbarState, { editor });
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            updateToolbarState();
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          CAN_UNDO_COMMAND,
          payload => {
            setCanUndo(payload);
            return false;
          },
          COMMAND_PRIORITY_LOW
        ),
        editor.registerCommand(
          CAN_REDO_COMMAND,
          payload => {
            setCanRedo(payload);
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor, updateToolbarState]
  );

  const applyBlockType = nextBlockType => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      if (nextBlockType === blockType) {
        $setBlocksType(selection, () => $createParagraphNode());
        return;
      }

      switch (nextBlockType) {
        case BLOCK_TYPE_H1:
        case BLOCK_TYPE_H2:
        case BLOCK_TYPE_H3:
          $setBlocksType(selection, () => $createHeadingNode(nextBlockType));
          break;
        case BLOCK_TYPE_QUOTE:
          $setBlocksType(selection, () => $createQuoteNode());
          break;
        case BLOCK_TYPE_CODE:
          $setBlocksType(selection, () => $createCodeNode());
          break;
        default:
          $setBlocksType(selection, () => $createParagraphNode());
      }
    });
  };

  const toggleHighlight = () => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const anchorNode = selection.anchor.getNode();
      const markParent = $findMatchingParent(anchorNode, node => $isMarkNode(node));
      if (markParent) {
        $unwrapMarkNode(markParent);
      } else {
        $wrapSelectionInMarkNode(selection, selection.isBackward(), generateMarkId());
      }
    });
  };

  const toggleLink = () => {
    if (isLink) {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      return;
    }
    setLinkPopoverUrl('');
  };

  const applyLinkUrl = () => {
    const url = linkPopoverUrl?.trim();
    setLinkPopoverUrl(null);
    if (!url) return;
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
  };

  const insertTable = () => {
    editor.dispatchCommand(INSERT_TABLE_COMMAND, { rows: '3', columns: '3', includeHeaders: true });
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const onFileSelected = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsUploading(true);
    try {
      const fileUrl = await UploadFile(imageFolder, file);
      editor.update(() => {
        const node = file.type.startsWith('image/')
          ? $createImageNode({ src: fileUrl, alt: file.name })
          : $createFileLinkNode({ fileUrl, fileName: file.name, fileType: file.type });
        $insertNodes([node]);
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="editor-toolbar">
      <select
        className="editor-toolbar-select"
        value={blockType}
        onChange={event => applyBlockType(event.target.value)}
        title="Block type"
      >
        {BLOCK_TYPE_OPTIONS.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <ToolbarDivider />

      <ToolbarButton
        active={isBold}
        title="Bold"
        label={<FontAwesomeIcon icon="bold" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
      />
      <ToolbarButton
        active={isItalic}
        title="Italic"
        label={<FontAwesomeIcon icon="italic" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
      />
      <ToolbarButton
        active={isUnderline}
        title="Underline"
        label={<FontAwesomeIcon icon="underline" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
      />
      <ToolbarButton
        active={isStrikethrough}
        title="Strikethrough"
        label={<FontAwesomeIcon icon="strikethrough" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
      />
      <ToolbarButton
        active={isInlineCode}
        title="Inline code"
        label={<FontAwesomeIcon icon="code" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
      />
      <ToolbarButton
        active={isSuperscript}
        title="Superscript"
        label={<FontAwesomeIcon icon="superscript" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'superscript')}
      />
      <ToolbarButton
        active={isSubscript}
        title="Subscript"
        label={<FontAwesomeIcon icon="subscript" />}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'subscript')}
      />
      <ToolbarButton
        active={isHighlight}
        title="Highlight"
        label={<FontAwesomeIcon icon="highlighter" />}
        onClick={toggleHighlight}
      />

      <ToolbarDivider />

      <ToolbarButton
        active={isUnorderedList}
        title="Bullet list"
        label={<FontAwesomeIcon icon="list-ul" />}
        onClick={() =>
          editor.dispatchCommand(
            isUnorderedList ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
            undefined
          )
        }
      />
      <ToolbarButton
        active={isOrderedList}
        title="Numbered list"
        label={<FontAwesomeIcon icon="list-ol" />}
        onClick={() =>
          editor.dispatchCommand(
            isOrderedList ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
            undefined
          )
        }
      />
      <ToolbarButton
        title="Outdent"
        label={<FontAwesomeIcon icon="outdent" />}
        onClick={() => editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined)}
      />
      <ToolbarButton
        title="Indent"
        label={<FontAwesomeIcon icon="indent" />}
        onClick={() => editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined)}
      />

      <ToolbarDivider />

      <ToolbarButton
        title="Align left"
        label={<FontAwesomeIcon icon="align-left" />}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left')}
      />
      <ToolbarButton
        title="Align center"
        label={<FontAwesomeIcon icon="align-center" />}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center')}
      />
      <ToolbarButton
        title="Align right"
        label={<FontAwesomeIcon icon="align-right" />}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right')}
      />
      <ToolbarButton
        title="Justify"
        label={<FontAwesomeIcon icon="align-justify" />}
        onClick={() => editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify')}
      />

      <ToolbarDivider />

      <ToolbarButton active={isLink} title="Link" label={<FontAwesomeIcon icon="link" />} onClick={toggleLink} />
      <ToolbarButton
        title="Horizontal rule"
        label={<FontAwesomeIcon icon="grip-lines" />}
        onClick={() => editor.dispatchCommand(INSERT_HORIZONTAL_RULE_COMMAND, undefined)}
      />
      <ToolbarButton title="Insert table" label={<FontAwesomeIcon icon="table" />} onClick={insertTable} />
      <ToolbarButton
        active={false}
        title="Upload file"
        label={<FontAwesomeIcon icon={isUploading ? 'spinner' : 'paperclip'} spin={isUploading} />}
        onClick={isUploading ? undefined : openFilePicker}
      />

      <ToolbarDivider />

      <ToolbarButton
        title="Undo"
        label={<FontAwesomeIcon icon="undo" />}
        disabled={!canUndo}
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
      />
      <ToolbarButton
        title="Redo"
        label={<FontAwesomeIcon icon="redo" />}
        disabled={!canRedo}
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
      />

      <input ref={fileInputRef} type="file" onChange={onFileSelected} style={{ display: 'none' }} />
      {linkPopoverUrl !== null && (
        <span className="editor-link-popover">
          <input
            autoFocus
            type="text"
            placeholder="https://"
            value={linkPopoverUrl}
            onChange={event => setLinkPopoverUrl(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') applyLinkUrl();
              if (event.key === 'Escape') setLinkPopoverUrl(null);
            }}
          />
          <button type="button" onMouseDown={event => event.preventDefault()} onClick={applyLinkUrl}>
            Apply
          </button>
        </span>
      )}
    </div>
  );
};

ToolbarPlugin.propTypes = {
  imageFolder: PropTypes.string.isRequired
};

export default ToolbarPlugin;
